import { Inject, Injectable } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';


import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { DataConnectorsApi } from '../../../api/externalData/data-connectors.api';
import { DataConnectorTypesEnum } from '../../../api/externalData/enums/data-connector-types.enum';

import { DataConnectorModel } from '../models/data-connector.model';
import { GoogleAnalyticsAccountModel } from '../models/google-analytics-account.model';
import { EmailAccountConnectorApiService } from '../../../api/emailAccountConnector/email-account-connector.api.service';
import { DataConnectorApiModel } from '../../../api/externalData/models/data-connector-api.model';
import { EmailAccountConnectorApi } from '../../../api/emailAccountConnector/models/email-account-connector.api';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { EmailAccountConnectorConfigApi } from '../../../api/emailAccountConnector/models/email-account-connector-config.api';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';


@Injectable()
export class DataConnectionService {
    protected loadingDataConnector = new BehaviorSubject<boolean>(false);
    loadingDataConnector$: Observable<boolean> = this.loadingDataConnector.asObservable();
    private refreshDataConnectorSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    dataConnectors$: Observable<DataConnectorModel[]> = this.refreshDataConnectorSubject.pipe(
        switchMap(() => {
            return this.dataConnectorsApi.getAll().pipe(
                mergeMap(apiConnectors => {
                    const connectors = apiConnectors.map(apiConnector => new DataConnectorModel(apiConnector));

                    const requests: Observable<DataConnectorModel>[] = connectors.map(connector => {
                        if (connector.type === DataConnectorTypesEnum.GOOGLE_ANALYTICS || connector.type === DataConnectorTypesEnum.GOOGLE_ANALYTICS_4) {
                            return this.getGoogleAnalyticsInfo(connector);
                        }

                        return of(connector);
                    });

                    return forkJoin(requests);
                }))
                .pipe(defaultIfEmpty(null));
        })
    );

    googleAnalyticsDataConnectors$ = this.dataConnectors$.pipe(map(
        connectors => connectors?.filter(connector =>
            connector.type === DataConnectorTypesEnum.GOOGLE_ANALYTICS || connector.type === DataConnectorTypesEnum.GOOGLE_ANALYTICS_4
        )
    ));

    protected loadingEmailAccountConnector = new BehaviorSubject<boolean>(false);
    loadingEmailAccountConnector$: Observable<boolean> = this.loadingEmailAccountConnector.asObservable();
    private refreshEmailAccountConnectorSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    emailAccountConnectors$: Observable<EmailAccountConnectorApi[]> = this.refreshEmailAccountConnectorSubject.pipe(
        switchMap(() => this.currentUserHasEmailAccountConnectorAccessOnEnvironment()),
        switchMap((hasAccess) => {
            this.loadingEmailAccountConnector.next(true);
            if (hasAccess) {
                return this.emailAccountConnectorApi.getAll();
            } else {
                return of([] as EmailAccountConnectorApi[]);
            }
        }),
        tap(() => this.loadingEmailAccountConnector.next(false)),
        shareReplay(1),
        catchError(err => {
            if (err.status === 404) {
                return of([]);
            }
            return throwError(() => new Error(err));
        })
    );

    emailAccountConfig$ = this.currentUserHasEmailAccountConnectorAccessOnEnvironment().pipe(
        switchMap((hasAccess) => {
            if (hasAccess) {
                return this.emailAccountConnectorApi.getEmailAccountConnectorConfig();
            } else {
                return of(null);
            }
        }),
        catchError(err => {
            if (err.status === 404) {
                return of({
                    sharedConnectorLimit: 0,
                    privateConnectorLimit: 0
                } as EmailAccountConnectorConfigApi);
            }
        }),
        shareReplay(1)
    );

    hasEmailAccountProvision$ = this.emailAccountConfig$.pipe(map(config => config && (config.privateConnectorLimit > 0 || config.sharedConnectorLimit > 0)));
    hasEmailAccountConnectors$ = this.emailAccountConnectors$.pipe(map(connectors => !!connectors.length));
    emailAccountConnectorsForCurrentUser$ = this.emailAccountConnectors$.pipe(
        switchMap(connectors => this.currentUserStateService.getUser().pipe(
            map(user => connectors.filter(
                connector => connector.privacyLevel === EmailAccountConnectorPrivacyLevelEnum.SHARED
                    || connector.userId === user.id))
        ))
    );
    userEmailAccountConnectionsRequireReAuth$ = this.currentUserHasEmailAccountConnectorAccessOnEnvironment().pipe(
        switchMap(hasAccess => {
            if (hasAccess) {
                return this.emailAccountConnectorsForCurrentUser$.pipe(
                    map(connectors => connectors.some(connector => connector.grant?.requiresReAuthentication))
                );
            }
            return of(false);
        })
    );

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private dataConnectorsApi: DataConnectorsApi,
        private emailAccountConnectorApi: EmailAccountConnectorApiService,
        private configService: ConfigService,
        private currentUserStateService: CurrentUserStateService,
    ) {}


    private getGoogleAnalyticsInfo(dataConnector: DataConnectorModel): Observable<DataConnectorModel> {

        return this.dataConnectorsApi.getGoogleAnalyticsAccounts(dataConnector.id).pipe(
            mergeMap(
                accounts => {
                    const requests: Observable<GoogleAnalyticsAccountModel>[] = [];

                    if (dataConnector.type === DataConnectorTypesEnum.GOOGLE_ANALYTICS) {
                        accounts.forEach(account => {
                            requests.push(
                                this.dataConnectorsApi.getGoogleAnalyticsProfiles(dataConnector.id, account.id)
                                    .pipe(
                                        map(profiles => {
                                            return new GoogleAnalyticsAccountModel(account, profiles);
                                        }),
                                        catchError((error, rejectedObservable) => {
                                            return of(account as GoogleAnalyticsAccountModel);
                                        })
                                    )
                            );
                        });

                        return forkJoin(requests);
                    } else {
                        return of(accounts as GoogleAnalyticsAccountModel[]);
                    }
                }
            ))
            .pipe(
                map(
                    accountProfiles => {
                        dataConnector.googleAnalyticsAccounts = accountProfiles;
                        return dataConnector;
                    }
                ),
                catchError((error, rejectedObservable) => {
                    return of(dataConnector);
                })
            );
    }

    openDataConnectionsNewWindow() {
        window.open(this.appBaseHref + 'DataConnector', '_blank');
    }

    refreshDataConnectors(): void {
        this.refreshDataConnectorSubject.next();
    }

    refreshEmailAccountConnectors(): void {
        this.refreshEmailAccountConnectorSubject.next();
    }

    deleteDataConnector(id: number) {
        return this.dataConnectorsApi.deleteDataConnector(id);
    }

    patchDataConnector(dataConnectorId: number, dataConnectorModel: DataConnectorApiModel): Observable<void> {
        return this.dataConnectorsApi.patchDataConnector(dataConnectorId, dataConnectorModel);
    }

    createDataConnector(dataConnectorModel: DataConnectorApiModel, apiKeyFile: File): Observable<number> {
        return this.dataConnectorsApi.createDataConnector(dataConnectorModel, apiKeyFile);
    }

    buildRedirectUrl(emailAccountConnector: EmailAccountConnectorApi): Observable<string> {
        return this.emailAccountConnectorApi.buildRedirectUrl(emailAccountConnector);
    }

    createEmailAccountConnector(emailAccountConnector: EmailAccountConnectorApi, code: string, state: string): Observable<number> {
        return this.emailAccountConnectorApi.createEmailAccountConnector(emailAccountConnector, code, state);
    }

    updateEmailAccountConnector(id: number, emailAccountConnector: EmailAccountConnectorApi): Observable<number> {
        return this.emailAccountConnectorApi.updateById(id, emailAccountConnector);
    }

    deleteEmailAccountConnector(id: number) {
        return this.emailAccountConnectorApi.deleteById(id);
    }

    /**
     * Does the current user
     */
    currentUserHasEmailAccountConnectorAccessOnEnvironment(): Observable<boolean> {
        return this.currentUserStateService.getAccount().pipe(
            filter(account => !!account),
            switchMap(account => of(this.configService.accountHasAccessToThirdPartyPitchingOnEnvironment(account.id)))
        );
    }
}
